import {
  ArrowLeft,
  Settings as SettingsIcon,
  Upload,
  ZoomIn,
  ZoomOut,
} from "lucide-react";
import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  useExtractedPatterns,
  useGenerationStatus,
  usePatternGeneration,
} from "../../hooks/useWFC";
import type { PatternGenerationParams } from "../../types/wfc";

// Do we need to use any of these ?? We did in an old implementation of this page
// import { useQuery, useMutation } from '@tanstack/react-query';
// import { Settings } from 'lucide-react';
// import { PageLayout } from '../components/layout/PageLayout';
// import { Card } from '../components/ui/Card';
// import { LoadingOverlay } from '../components/LoadingOverlay';
// import { ErrorMessage } from '../components/ui/ErrorMessage';
// import { AnimatedGenerationPreview } from '../components/generation/AnimatedGenerationPreview';
// import { GenerationSettings } from '../components/generation/GenerationSettings';

interface PatternProperties {
  outputWidth: number;
  outputHeight: number;
  patternSize: number;
  overlap: number;
  symmetry: boolean;
  allowRotations: boolean;
  temperature: number; // For controlling randomness in WFC
  iterations: number; // Maximum WFC iterations
}

export default function PatternPage() {
  const [showProperties, setShowProperties] = useState(true);
  const patternGeneration = usePatternGeneration();
  const generationStatus = useGenerationStatus(
    patternGeneration.data?.id ?? null,
  );
  const patterns = useExtractedPatterns(patternGeneration.data?.id ?? null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [properties, setProperties] = useState<
    PatternProperties & PatternGenerationParams
  >({
    outputWidth: 256,
    outputHeight: 256,
    patternSize: 3,
    overlap: 1,
    symmetry: true,
    allowRotations: true,
    temperature: 0.1,
    iterations: 1000,
  });

  const [zoom, setZoom] = useState(1);

  return (
    <div className="h-screen flex">
      {/* Main Content */}
      <div className="flex-1 overflow-auto p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Link to="/" className="btn btn-ghost btn-sm">
              <ArrowLeft size={20} />
              <span className="ml-2">Back to Home</span>
            </Link>
            <h1 className="text-2xl font-bold">Pattern Generator</h1>
          </div>

          <div className="flex items-center gap-2">
            {/* Zoom Controls */}
            <div className="btn-group">
              <button
                className="btn btn-sm"
                onClick={() => setZoom((z) => Math.max(0.25, z - 0.25))}
                aria-label="Zoom out"
              >
                <ZoomOut size={16} />
              </button>
              <button
                className="btn btn-sm"
                aria-label={`Current zoom level: ${Math.round(zoom * 100)}%`}
              >
                {Math.round(zoom * 100)}%
              </button>
              <button
                className="btn btn-sm"
                onClick={() => setZoom((z) => Math.min(4, z + 0.25))}
                aria-label="Zoom in"
              >
                <ZoomIn size={16} />
              </button>
            </div>

            <button
              className="btn btn-ghost btn-circle"
              onClick={() => setShowProperties(!showProperties)}
              aria-label="Toggle pattern properties"
            >
              <SettingsIcon size={20} />
            </button>
          </div>
        </div>

        {/* Upload Area */}
        <div className="border-2 border-dashed border-base-content/20 rounded-lg p-12 text-center">
          <div className="max-w-xl mx-auto">
            <Upload className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <h3 className="text-lg font-medium mb-2">Upload Source Image</h3>
            <p className="text-base-content/60 mb-4">
              The pattern generator will analyze this image to extract patterns
            </p>
            <div className="flex flex-col gap-4 items-center">
              <label htmlFor="source-image-upload" className="sr-only">
                Upload source image
              </label>
              <input
                id="source-image-upload"
                type="file"
                className="file-input file-input-bordered file-input-primary w-full max-w-xs"
                accept="image/*"
                aria-label="Upload source image"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) setSelectedFile(file);
                }}
              />
            </div>
          </div>
        </div>

        {/* Preview Area - Will show extracted patterns and generation preview */}
        <div className="mt-6 space-y-6">
          <div className="bg-base-200 rounded-lg p-4">
            <h3 className="font-medium mb-4">Extracted Patterns</h3>
            <div className="grid grid-cols-6 gap-2">
              {patterns.data?.map((pattern, index) => (
                <div
                  key={index}
                  className="bg-base-300 rounded aspect-square"
                  style={{
                    backgroundImage: `url(${pattern.imageUrl})`,
                    backgroundSize: "cover",
                  }}
                />
              ))}
            </div>
          </div>

          <div className="bg-base-200 rounded-lg p-4">
            <h3 className="font-medium mb-4">Generation Preview</h3>
            <div
              className="bg-base-300 rounded-lg aspect-square"
              style={{
                transform: `scale(${zoom})`,
                transformOrigin: "top left",
              }}
            >
              {generationStatus.data?.status === "completed" &&
              generationStatus.data.outputUrl ? (
                <img
                  src={generationStatus.data.outputUrl}
                  alt="Generated pattern"
                  className="rounded-lg"
                  style={{
                    transform: `scale(${zoom})`,
                    transformOrigin: "top left",
                  }}
                />
              ) : (
                <div className="aspect-square flex items-center justify-center">
                  {generationStatus.data?.status === "pending" ? (
                    <div className="loading loading-spinner loading-lg" />
                  ) : generationStatus.data?.status === "failed" ? (
                    <div className="text-error">
                      {generationStatus.data.error || "Generation failed"}
                    </div>
                  ) : null}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Properties Panel */}
      {showProperties && (
        <div className="w-80 border-l border-base-content/10 bg-base-200 p-4 overflow-y-auto">
          <div className="sticky top-0">
            <h2 className="font-bold mb-4">Properties</h2>

            <div className="space-y-4">
              <div className="form-control">
                <label className="label" htmlFor="outputWidth">
                  <span className="label-text">Output Width</span>
                </label>
                <input
                  id="outputWidth"
                  type="number"
                  value={properties.outputWidth}
                  onChange={(e) =>
                    setProperties({
                      ...properties,
                      outputWidth: parseInt(e.target.value),
                    })
                  }
                  className="input input-bordered"
                  min={32}
                  aria-label="Output Width"
                />
              </div>

              <div className="form-control">
                <label className="label" htmlFor="outputHeight">
                  <span className="label-text">Output Height</span>
                </label>
                <input
                  id="outputHeight"
                  type="number"
                  value={properties.outputHeight}
                  onChange={(e) =>
                    setProperties({
                      ...properties,
                      outputHeight: parseInt(e.target.value),
                    })
                  }
                  className="input input-bordered"
                  min={32}
                  aria-label="Output Height"
                />
              </div>

              <div className="form-control">
                <label className="label" htmlFor="patternSize">
                  <span className="label-text">Pattern Size</span>
                </label>
                <input
                  id="patternSize"
                  type="number"
                  value={properties.patternSize}
                  onChange={(e) =>
                    setProperties({
                      ...properties,
                      patternSize: parseInt(e.target.value),
                    })
                  }
                  className="input input-bordered"
                  min={2}
                  aria-label="Pattern Size"
                />
                <label className="label">
                  <span className="label-text-alt">
                    Larger patterns capture more structure but need more samples
                  </span>
                </label>
              </div>

              <div className="form-control">
                <label className="label" htmlFor="overlap">
                  <span className="label-text">Overlap Size</span>
                </label>
                <input
                  id="overlap"
                  type="number"
                  value={properties.overlap}
                  onChange={(e) =>
                    setProperties({
                      ...properties,
                      overlap: parseInt(e.target.value),
                    })
                  }
                  className="input input-bordered"
                  min={1}
                  aria-label="Overlap Size"
                />
              </div>

              <div className="form-control">
                <label className="label" htmlFor="temperature">
                  <span className="label-text">Temperature</span>
                </label>
                <input
                  id="temperature"
                  type="range"
                  value={properties.temperature}
                  onChange={(e) =>
                    setProperties({
                      ...properties,
                      temperature: parseFloat(e.target.value),
                    })
                  }
                  className="range"
                  min={0}
                  max={1}
                  step={0.1}
                  aria-label="Temperature"
                />
                <label className="label">
                  <span className="label-text-alt">
                    Higher values = more randomness
                  </span>
                </label>
              </div>

              <div className="form-control">
                <label className="label cursor-pointer" htmlFor="symmetry">
                  <span className="label-text">Enable Symmetry</span>
                  <input
                    id="symmetry"
                    type="checkbox"
                    checked={properties.symmetry}
                    onChange={(e) =>
                      setProperties({
                        ...properties,
                        symmetry: e.target.checked,
                      })
                    }
                    className="checkbox"
                    aria-label="Enable Symmetry"
                  />
                </label>
              </div>

              <div className="form-control">
                <label
                  className="label cursor-pointer"
                  htmlFor="allowRotations"
                >
                  <span className="label-text">Allow Rotations</span>
                  <input
                    id="allowRotations"
                    type="checkbox"
                    checked={properties.allowRotations}
                    onChange={(e) =>
                      setProperties({
                        ...properties,
                        allowRotations: e.target.checked,
                      })
                    }
                    className="checkbox"
                    aria-label="Allow Rotations"
                  />
                </label>
              </div>

              <button
                className="btn btn-primary w-full"
                onClick={() => {
                  if (!selectedFile) return;
                  patternGeneration.mutateAsync({
                    file: selectedFile,
                    params: {
                      patternSize: properties.patternSize,
                      overlap: properties.overlap,
                      symmetry: properties.symmetry,
                      allowRotations: properties.allowRotations,
                      temperature: properties.temperature,
                      iterations: properties.iterations,
                      outputWidth: properties.outputWidth,
                      outputHeight: properties.outputHeight,
                    },
                  });
                }}
                disabled={!selectedFile || patternGeneration.isPending}
              >
                Generate Pattern
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
